import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  WorkSecurityArea,
  fetchWorkSecurityAreas,
} from "services/work-security-areas.service";
import { WorkSecuritySettings } from "types/WorkSecuritySettings";
import { StatusBanner } from "../ui";
import AiQuestionsCard from "./work-security/AiQuestionsCard";
import AreaDocuments from "./work-security/AreaDocuments";
import AreasCard from "./work-security/AreasCard";
import LoadingCard from "./work-security/LoadingCard";

interface ScopeSwitchProps {
  areas: WorkSecurityArea[];
  /** Empty: the whole company. */
  selectedAreaId: string;
  onSelect: (areaId: string) => void;
}

/** "Settings apply to: Whole company | <area> | …" */
function ScopeSwitch({ areas, selectedAreaId, onSelect }: ScopeSwitchProps) {
  const { t } = useTranslation();
  const choices = [
    { id: "", name: t("work_security_settings_scope_company") },
    ...areas,
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-600">
        {t("work_security_settings_scope_label")}:
      </span>
      <div className="inline-flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
        {choices.map(({ id, name }) => (
          <button
            key={id}
            type="button"
            aria-pressed={id === selectedAreaId}
            onClick={() => onSelect(id)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ${
              id === selectedAreaId
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
}

interface WorkSecurityTabProps {
  companyEntity: WorkSecuritySettings;
  /** Reloads the company record. */
  onRefresh: () => Promise<unknown>;
}

/** Procedures: instruction areas, the documents made for them, and the AI test questions. */
export default function WorkSecurityTab({
  companyEntity,
  onRefresh,
}: WorkSecurityTabProps) {
  const { t } = useTranslation();
  const { companyId } = useParams() as { companyId: string };

  const [areas, setAreas] = useState<WorkSecurityArea[]>([]);
  const [areasFailed, setAreasFailed] = useState(false);
  const [areasLoaded, setAreasLoaded] = useState(false);

  const loadAreas = useCallback(async () => {
    try {
      const data = await fetchWorkSecurityAreas(companyId, {
        includeArchived: true,
      });
      setAreas(data.areas);
      setAreasFailed(false);
    } catch (err) {
      console.error("Failed to load instruction areas", err);
      setAreasFailed(true);
    } finally {
      setAreasLoaded(true);
    }
  }, [companyId]);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  // Documents can be scoped to one area only when there is a choice between several.
  const activeAreas = areas.filter((area) => !area.isArchived);
  const [chosenAreaId, setChosenAreaId] = useState("");
  const selectedArea =
    activeAreas.length > 1
      ? activeAreas.find((area) => area.id === chosenAreaId)
      : undefined;
  const areaId = selectedArea?.id ?? "";

  return (
    <div className="space-y-5">
      {areasFailed && (
        <StatusBanner
          tone="error"
          title={t("work_security_areas_load_error")}
        />
      )}

      {areasLoaded ? (
        <AreasCard companyId={companyId} areas={areas} onChanged={loadAreas} />
      ) : (
        <LoadingCard />
      )}

      {activeAreas.length > 1 && (
        <ScopeSwitch
          areas={activeAreas}
          selectedAreaId={areaId}
          onSelect={setChosenAreaId}
        />
      )}

      <AreaDocuments
        key={areaId}
        companyId={companyId}
        area={selectedArea}
        companyEntity={companyEntity}
        refreshCompany={onRefresh}
      />

      <AiQuestionsCard
        companyId={companyId}
        enabled={Boolean(companyEntity.workSecurityAiQuestionsEnabled)}
        onSaved={onRefresh}
      />
    </div>
  );
}
